import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FaceDetection } from '@mediapipe/face_detection';
import CandidateInterviewRoom from './CandidateInterviewRoom';
import CodingExamRoom from './CodingExamRoom';
import './CandidateLanding.css';
import { getPublicApiOrigin } from '../config/env';
import {
  startInterviewPrep,
  getInterviewPrep,
  claimInterviewResources,
  isInterviewPrepHandedOff,
  isStreamLive,
  registerInterviewPrepInstances,
  releaseClaimedInterviewResources,
  releaseInterviewPrep,
} from '../utils/interviewPrep';
import { requestExamFullscreen, requestEntireScreenShare } from '../utils/fullscreen';

const CandidateLanding = () => {
  const { invite_token } = useParams();
  const interviewResourcesRef = useRef(null);
  const [campaign, setCampaign] = useState(null);
  const [candidateName, setCandidateName] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [codingResumeState, setCodingResumeState] = useState(null);
  const [examPhase, setExamPhase] = useState(null); // interview | coding for interview_coding
  const [step, setStep] = useState(1); // 1: Landing, 2: Terms, 3: System Check, 4: Interview
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showExpired, setShowExpired] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  /** Heading for other 409s (e.g. interrupted too many times / closed unfinished). */
  const [blockedTitle, setBlockedTitle] = useState(null);
  const [resumeMode, setResumeMode] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [showAlreadyActive, setShowAlreadyActive] = useState(false);
  const [blockedReason, setBlockedReason] = useState('same_browser');
  const broadcastChannelRef = useRef(null);

  const getStoredTabToken = useCallback(() => {
    try {
      const existing = localStorage.getItem('wiztok_active_session');
      if (!existing) return null;
      const session = JSON.parse(existing);
      if (session.invite_id !== invite_token) return null;
      const age = Date.now() - (session.started_at || 0);
      if (age >= 2 * 60 * 60 * 1000) {
        localStorage.removeItem('wiztok_active_session');
        sessionStorage.removeItem('wiztok_owner_tab');
        return null;
      }
      return session.token || null;
    } catch {
      localStorage.removeItem('wiztok_active_session');
      sessionStorage.removeItem('wiztok_owner_tab');
      return null;
    }
  }, [invite_token]);

  useEffect(() => {
    const existing = localStorage.getItem('wiztok_active_session');
    if (existing) {
      try {
        const session = JSON.parse(existing);
        if (session.invite_id === invite_token) {
          const age = Date.now() - (session.started_at || 0);
          if (age >= 2 * 60 * 60 * 1000) {
            localStorage.removeItem('wiztok_active_session');
            sessionStorage.removeItem('wiztok_owner_tab');
          }
        } else {
          localStorage.removeItem('wiztok_active_session');
        }
      } catch {
        localStorage.removeItem('wiztok_active_session');
        sessionStorage.removeItem('wiztok_owner_tab');
      }
    }

    const channel = new BroadcastChannel('wiztok_interview');
    broadcastChannelRef.current = channel;
    channel.onmessage = (e) => {
      if (e.data?.type === 'session_started' && e.data?.invite_id === invite_token) {
        const ownerTab = sessionStorage.getItem('wiztok_owner_tab');
        if (ownerTab && ownerTab === e.data?.owner_tab_id) return;
        setBlockedReason('same_browser');
        setShowAlreadyActive(true);
        setLoading(false);
      }
      if (e.data?.type === 'session_ended') {
        setShowAlreadyActive(false);
      }
    };

    return () => {
      channel.close();
      if (broadcastChannelRef.current === channel) {
        broadcastChannelRef.current = null;
      }
    };
  }, [invite_token]);

  const loadInterviewInfo = useCallback(async () => {
    if (showAlreadyActive) return;

    try {
      setLoading(true);
      const tabToken = getStoredTabToken();
      const response = await axios.get(
        `${getPublicApiOrigin()}/api/v1/public/interview/${invite_token}/info`,
        { params: tabToken ? { tab_token: tabToken } : {} }
      );

      setCampaign(response.data.campaign);
      setCandidateName(response.data.candidate_name);
      setSessionId(response.data.session_id);
      const examType = response.data.campaign?.exam_type;
      const phase = response.data.phase || (examType === 'interview_coding' ? 'interview' : null);
      setExamPhase(phase);
      if (examType === 'coding' || (examType === 'interview_coding' && phase === 'coding')) {
        setCodingResumeState({
          currentQuestionIndex: response.data.current_question_index ?? 0,
          draftCode: response.data.draft_code ?? null,
          language: response.data.language ?? null,
        });
      }

      localStorage.setItem('session_id', response.data.session_id);
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('candidate_session', JSON.stringify({
        session_id: response.data.session_id,
        access_token: response.data.access_token,
      }));

      const shouldAutoResume =
        response.data.resume === true ||
        response.data.session_status === 'in_progress';
      // Interrupted exam (lost connection, closed browser, laptop off): answers so far are kept.
      const { answered_count: answered, total_questions: total } = response.data;
      const isCodingExam =
        examType === 'coding' ||
        (examType === 'interview_coding' && phase === 'coding');
      const progressNote = total
        ? ` — ${answered} of ${total} ${isCodingExam ? 'problems' : 'questions'} answered`
        : '';
      const resumeMessage = response.data.takeover
        ? `Continuing on this device — your earlier device was disconnected${progressNote}`
        : `Resuming your ${isCodingExam ? 'coding exam' : 'interview'} from where you left off${progressNote}`;

      if (shouldAutoResume && tabToken) {
        try {
          const stored = JSON.parse(localStorage.getItem('wiztok_active_session') || 'null');
          if (stored?.owner_tab_id) {
            sessionStorage.setItem('wiztok_owner_tab', stored.owner_tab_id);
          }
        } catch {
          /* ignore */
        }
        setResumeMode(true);
        setInfoMessage(resumeMessage);
        setStep(4);
        return;
      }

      if (response.data.resume === true) {
        // New browser/device: go through the camera, mic and screen checks again first.
        setResumeMode(true);
        setInfoMessage(resumeMessage);
      }

      if (response.data.attempt_number > 1) {
        setInfoMessage((prev) =>
          prev
            ? `${prev}. Starting fresh with new questions.`
            : 'Starting fresh with new questions'
        );
      }
    } catch (err) {
      if (err.response?.status === 410) {
        setError(err.response.data.detail || 'This interview link has expired.');
        setShowExpired(true);
        setLoading(false);
        return;
      }

      if (err.response?.status === 409) {
        const detail = err.response.data.detail || '';
        const lower = typeof detail === 'string' ? detail.toLowerCase() : '';
        if (lower.includes('currently in progress') || lower.includes('another device')) {
          setBlockedReason('other_device');
          setShowAlreadyActive(true);
          setLoading(false);
          return;
        }
        setError(detail || 'You have already completed this interview.');
        if (lower.includes('already completed') || !detail) setShowCompleted(true);
        else setBlockedTitle('Interview unavailable');
        setLoading(false);
        return;
      }

      let errorMessage = 'Invalid invite link';
      if (err.response?.data) {
        const detail = err.response.data.detail;
        if (detail?.code === 'not_open_yet' && detail.opens_at) {
          // Batch window not open yet: show the date in the candidate's own timezone.
          const opens = new Date(detail.opens_at);
          errorMessage = Number.isNaN(opens.getTime())
            ? detail.message
            : `This interview opens on ${opens.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.`;
        } else if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail.map(e => e.msg || e).join(', ');
        } else if (err.response.data.detail?.msg) {
          errorMessage = err.response.data.detail.msg;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [invite_token, showAlreadyActive, getStoredTabToken]);

  useEffect(() => {
    if (!showAlreadyActive) {
      loadInterviewInfo();
    }
  }, [loadInterviewInfo, showAlreadyActive]);

  useEffect(() => {
    if (step !== 4) {
      releaseClaimedInterviewResources();
      interviewResourcesRef.current = null;
    }
  }, [step]);

  if (showAlreadyActive) {
    const isSameBrowser = blockedReason === 'same_browser';
    return (
      <div
        className="candidate-landing"
        style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 40%, #C7D2FE 100%)' }}
      >
        <div className="landing-container" style={{ textAlign: 'center', maxWidth: 520, margin: '0 auto' }}>
          <div
            style={{
              width: 64,
              height: 64,
              margin: '0 auto 20px',
              borderRadius: '50%',
              background: '#FEF3C7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" width="32" height="32" aria-hidden>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
            Interview already in progress
          </h2>
          <p style={{ fontSize: 15, color: '#4B5563', lineHeight: 1.7, marginBottom: 16 }}>
            {isSameBrowser
              ? 'Your interview session is currently active in another tab or window. Please return to that tab to continue your interview.'
              : 'This interview is open on another device or browser. If that device closed or lost its connection, wait two minutes and try again — your answers so far are saved.'}
          </p>
          {!isSameBrowser && (
            <button
              type="button"
              onClick={() => setShowAlreadyActive(false)}
              style={{
                marginBottom: 16, padding: '10px 22px', borderRadius: 8, border: 'none',
                background: '#6366F1', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}
            >
              Try again
            </button>
          )}
          <p style={{ fontSize: 13, color: '#9CA3AF', lineHeight: 1.6 }}>
            If you believe this is an error, please contact{' '}
            <a href="mailto:hiring@feuji.com" style={{ color: '#6366F1', textDecoration: 'none' }}>
              hiring@feuji.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="candidate-landing">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="candidate-landing">
        <div className="error-state">
          <h2>
            {showExpired
              ? 'Link Expired'
              : showCompleted
                ? 'Interview Completed'
                : blockedTitle || 'Invalid Invite Link'}
          </h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Step 1: Landing Page
  if (step === 1) {
    return (
      <div
        className="candidate-landing"
        style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 40%, #C7D2FE 100%)' }}
      >
        <div className="landing-container">
          <div className="landing-header">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  background: '#6366F1',
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(99,102,241,.4)',
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" width="18" height="18" aria-hidden>
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <span style={{ fontSize: 22, fontWeight: 700, color: '#6366F1', letterSpacing: '-.3px' }}>Wizetok</span>
            </div>
            <h2>
              {campaign?.exam_type === 'coding'
                ? 'Welcome to Your Coding Exam'
                : campaign?.exam_type === 'interview_coding'
                  ? 'Welcome to Your AI Interview + Coding Exam'
                  : 'Welcome to Your AI Interview'}
            </h2>
            {infoMessage && (
              <p style={{ marginTop: 12, fontSize: 14, color: '#4338CA', fontWeight: 600 }}>
                {infoMessage}
              </p>
            )}
          </div>

          <div className="campaign-info-card">
            <h3>
              Hi {candidateName}, you've been invited to{' '}
              {campaign?.exam_type === 'coding' ? 'take a coding exam for:' : 'interview for:'}
            </h3>
            <div className="campaign-details">
              <div className="detail-item">
                <span className="icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <path d="M20 7h-9M14 17H5M17 12H8" />
                    <circle cx="17" cy="7" r="3" />
                    <circle cx="7" cy="17" r="3" />
                  </svg>
                </span>
                <span className="label">{campaign?.job_role}</span>
              </div>
              <div className="detail-item">
                <span className="icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <line x1="18" y1="20" x2="18" y2="10" />
                    <line x1="12" y1="20" x2="12" y2="4" />
                    <line x1="6" y1="20" x2="6" y2="14" />
                  </svg>
                </span>
                <span className="label">
                  {campaign?.questions_count || 3}{' '}
                  {campaign?.exam_type === 'coding' ? 'Problems' : 'Questions'}
                </span>
              </div>
              <div className="detail-item">
                <span className="icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </span>
                <span className="label">
                  Estimated Time:{' '}
                  {campaign?.estimated_duration || (campaign?.exam_type === 'coding' ? '45 minutes' : '20–30 minutes')}
                </span>
              </div>
            </div>
            {campaign?.estimated_duration && (
              <p className="duration-note" style={{ margin: '12px 0 0', fontSize: 13, color: '#6B7280' }}>
                This {campaign?.exam_type === 'coding' ? 'exam' : 'interview'} takes approximately {campaign.estimated_duration}.
              </p>
            )}
          </div>

          <div className="what-to-expect">
            <h3>What to expect:</h3>
            <ul>
              {campaign?.exam_type === 'coding' ? (
                <>
                  <li>✓ Timed coding problems in the browser</li>
                  <li>✓ Run sample tests before you submit</li>
                  <li>✓ Proctored for integrity</li>
                  <li>✓ Results shared with hiring team</li>
                </>
              ) : campaign?.exam_type === 'interview_coding' ? (
                <>
                  <li>✓ AI-powered interview with voice interaction</li>
                  <li>✓ Followed by a timed coding round</li>
                  <li>✓ Proctored for integrity</li>
                  <li>✓ Results shared with hiring team</li>
                </>
              ) : (
                <>
                  <li>✓ AI-powered interview with voice interaction</li>
                  <li>✓ Technical and behavioral questions</li>
                  <li>✓ Proctored for integrity</li>
                  <li>✓ Results shared with hiring team</li>
                </>
              )}
            </ul>
          </div>

          <button
            type="button"
            onClick={() => setStep(2)}
            className="btn-large"
            style={{
              width: '100%',
              padding: '15px 32px',
              fontSize: 15,
              fontWeight: 600,
              color: 'white',
              background: 'linear-gradient(135deg, #6366F1, #4F46E5)',
              border: 'none',
              borderRadius: 10,
              cursor: 'pointer',
              fontFamily: 'inherit',
              boxShadow: '0 4px 14px rgba(99,102,241,.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'all .18s',
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="16" height="16" aria-hidden>
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            I'm Ready — Check My Setup
          </button>
        </div>
      </div>
    );
  }

  // Step 2: Terms & Conditions
  if (step === 2) {
    return (
      <TermsAndConditions
        onAccept={() => setStep(3)}
        onBack={() => {
          releaseInterviewPrep();
          setStep(1);
        }}
      />
    );
  }

  // Step 3: System Check
  if (step === 3) {
    return (
      <SystemCheck
        campaign={campaign}
        onStartInterview={(mediaStream) => {
          const sessionToken = crypto.randomUUID();
          const ownerTabId = crypto.randomUUID();
          sessionStorage.setItem('wiztok_owner_tab', ownerTabId);
          localStorage.setItem(
            'wiztok_active_session',
            JSON.stringify({
              token: sessionToken,
              invite_id: invite_token,
              owner_tab_id: ownerTabId,
              started_at: Date.now(),
            }),
          );

          let channel = broadcastChannelRef.current;
          if (!channel) {
            channel = new BroadcastChannel('wiztok_interview');
            broadcastChannelRef.current = channel;
          }
          channel.postMessage({
            type: 'session_started',
            token: sessionToken,
            invite_id: invite_token,
            owner_tab_id: ownerTabId,
          });

          interviewResourcesRef.current = claimInterviewResources({
            stream: mediaStream?.cameraStream || mediaStream,
            screenStream: mediaStream?.screenStream || null,
          });
          setStep(4);
        }}
      />
    );
  }

  // Step 4: Interview Room — coding exam when exam_type is coding, or
  // interview_coding after the AI phase advances to coding.
  if (step === 4) {
    if (!sessionId) {
      setStep(1);
      return null;
    }
    const examType = campaign?.exam_type;
    const inCodingPhase =
      examType === 'coding' ||
      (examType === 'interview_coding' && (examPhase === 'coding' || examPhase === 'done'));

    if (inCodingPhase) {
      return (
        <CodingExamRoom
          sessionIdFromLanding={sessionId}
          prepResources={interviewResourcesRef.current}
          isResuming={resumeMode || examType === 'interview_coding'}
          fullscreenRequired={!!campaign?.fullscreen_required}
          resumeState={codingResumeState}
        />
      );
    }
    return (
      <CandidateInterviewRoom
        sessionIdFromLanding={sessionId}
        prepResources={interviewResourcesRef.current}
        isResuming={resumeMode}
        fullscreenRequired={!!campaign?.fullscreen_required}
        expectsCodingPhase={examType === 'interview_coding'}
        onContinueToCoding={() => {
          setExamPhase('coding');
          setResumeMode(false);
          setCodingResumeState({
            currentQuestionIndex: 0,
            draftCode: null,
            language: null,
          });
        }}
      />
    );
  }

  return null;
};

const TermsAndConditions = ({ onAccept, onBack }) => {
  const [accepted, setAccepted] = useState({
    terms: false,
    recording: false,
    proctoring: false
  });

  useEffect(() => {
    startInterviewPrep();
  }, []);

  const canProceed = accepted.terms && accepted.recording && accepted.proctoring;

  return (
    <div className="candidate-landing">
      <div className="form-container">
        <h2>Interview Guidelines & Consent</h2>

        <div className="terms-content">
          <h3>📜 Terms & Conditions</h3>
          <div className="terms-text">
            <p><strong>Please read carefully before proceeding:</strong></p>
            <ul>
              <li>✓ This session will be recorded (camera + microphone)</li>
              <li>✓ Fullscreen mode is required — exiting will be logged</li>
              <li>✓ Entire-screen sharing is required</li>
              <li>✓ Proctoring monitors face, gaze, phones/objects, and tab switches</li>
              <li>✓ Copy/paste and blocked shortcuts are restricted</li>
              <li>✓ Multiple faces or identity mismatch will be flagged</li>
            </ul>

            <h4>DO's:</h4>
            <ul>
              <li>✓ Find a quiet, well-lit space</li>
              <li>✓ Look directly at the camera</li>
              <li>✓ Stay in fullscreen with screen share on</li>
              <li>✓ Speak clearly and confidently</li>
            </ul>

            <h4>DON'Ts:</h4>
            <ul>
              <li>✗ Don't switch tabs or windows</li>
              <li>✗ Don't exit fullscreen or stop screen share</li>
              <li>✗ Don't use phones or secondary devices</li>
              <li>✗ Don't have others present during the session</li>
              <li>✗ Don't use external resources or copy/paste from outside</li>
            </ul>

            <h4>Violations may result in:</h4>
            <ul>
              <li>⚠️ Warnings logged for review</li>
              <li>📊 Lower integrity score</li>
              <li>📝 Violations recorded in your report</li>
            </ul>
          </div>

          <div className="consent-checkboxes">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={accepted.terms}
                onChange={(e) => setAccepted(prev => ({ ...prev, terms: e.target.checked }))}
              />
              <span>I have read and agree to the terms</span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={accepted.recording}
                onChange={(e) => setAccepted(prev => ({ ...prev, recording: e.target.checked }))}
              />
              <span>I consent to recording</span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={accepted.proctoring}
                onChange={(e) => setAccepted(prev => ({ ...prev, proctoring: e.target.checked }))}
              />
              <span>I understand the proctoring rules</span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={onBack}
            style={{
              background: 'white',
              color: '#6B7280',
              border: '1px solid #E5E7EB',
              borderRadius: 10,
              padding: '12px 24px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              boxShadow: '0 1px 3px rgba(0,0,0,.06)',
            }}
          >
            Back
          </button>
          <button
            type="button"
            onClick={onAccept}
            disabled={!canProceed}
            style={{
              background: !canProceed ? '#A5B4FC' : 'linear-gradient(135deg, #6366F1, #4F46E5)',
              color: 'white',
              border: 'none',
              borderRadius: 10,
              padding: '12px 24px',
              fontSize: 14,
              fontWeight: 600,
              cursor: !canProceed ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              boxShadow: !canProceed ? 'none' : '0 4px 14px rgba(99,102,241,.4)',
              opacity: !canProceed ? 0.65 : 1,
            }}
          >
            I'm Ready - Start Interview
          </button>
        </div>
      </div>
    </div>
  );
};

const SystemCheck = ({ campaign, onStartInterview }) => {
  const [checks, setChecks] = useState({
    camera: false,
    microphone: false,
    internet: true,
    browser: true,
    face: false,
    screenShare: false,
  });
  const [checking, setChecking] = useState(true);
  const [micTest, setMicTest] = useState('idle'); // 'idle' | 'playing' | 'listening' | 'passed' | 'failed'
  const [screenShareBusy, setScreenShareBusy] = useState(false);
  const [screenShareError, setScreenShareError] = useState('');
  const [starting, setStarting] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const watchIntervalRef = useRef(null);
  const faceDetectionRef = useRef(null);
  const faceDetectionLoopRef = useRef(null);
  const isProcessingRef = useRef(false);
  const faceDetInitialized = useRef(false);

  useEffect(() => {
    if (faceDetInitialized.current) return;
    faceDetInitialized.current = true;
    performSystemCheck();
    return () => {
      if (watchIntervalRef.current) clearInterval(watchIntervalRef.current);
      if (faceDetectionLoopRef.current) clearInterval(faceDetectionLoopRef.current);
      if (isInterviewPrepHandedOff()) return;
      if (faceDetectionRef.current && faceDetectionRef.current !== getInterviewPrep().faceDetection) {
        try { faceDetectionRef.current.close(); } catch (_) {}
        faceDetectionRef.current = null;
      }
      const mediaStream = streamRef.current;
      const prepStream = getInterviewPrep().stream;
      if (mediaStream && mediaStream !== prepStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
      }
      const screen = screenStreamRef.current;
      const prepScreen = getInterviewPrep().screenStream;
      if (screen && screen !== prepScreen) {
        screen.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const performSystemCheck = async () => {
    try {
      const prep = getInterviewPrep();
      let stream = prep.stream?.active ? prep.stream : null;
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      }
      streamRef.current = stream;

      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) {
        setChecks(prev => ({ ...prev, camera: false, microphone: true }));
        return;
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setChecks(prev => ({ ...prev, microphone: true }));

      const onTrackEnded = () => {
        setChecks(prev => ({ ...prev, camera: false, face: false }));
      };
      videoTrack.onended = onTrackEnded;

      // Camera = we have a real video feed (dimensions available)
      const updateCameraFromVideo = () => {
        const video = videoRef.current;
        const vt = streamRef.current?.getVideoTracks()[0];
        const hasFeed = vt && vt.readyState === 'live' && vt.enabled && video?.videoWidth > 0 && video?.videoHeight > 0;
        setChecks(prev => ({ ...prev, camera: hasFeed }));
      };
      if (videoRef.current) {
        videoRef.current.onloadeddata = updateCameraFromVideo;
        if (videoRef.current.readyState >= 2) updateCameraFromVideo();
      }
      setTimeout(updateCameraFromVideo, 800);

      // Face = face actually visible (MediaPipe face detection)
      let faceDetection = faceDetectionRef.current || null;
      if (!faceDetection && typeof FaceDetection !== 'undefined') {
        try {
          faceDetection = new FaceDetection({
            locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/${file}`
          });
          faceDetection.setOptions({ model: 'short', minDetectionConfidence: 0.5 });
          registerInterviewPrepInstances({ faceDetection });
        } catch (e) {
          console.warn('Face detection init failed, face check will not run:', e);
        }
      }

      if (faceDetection) {
        faceDetectionRef.current = faceDetection;
        registerInterviewPrepInstances({ faceDetection });
        faceDetection.onResults((results) => {
          const faceVisible = results.detections && results.detections.length > 0;
          setChecks(prev => ({ ...prev, face: faceVisible }));
        });
      }

      const runFaceDetection = () => {
        const video = videoRef.current;
        if (!faceDetection || !video || isProcessingRef.current) return;
        if (video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) return;
        isProcessingRef.current = true;
        faceDetection.send({ image: video })
          .catch(() => {})
          .finally(() => { isProcessingRef.current = false; });
      };

      const startFaceDetectionLoop = () => {
        if (!faceDetection) return;
        runFaceDetection();
        faceDetectionLoopRef.current = setInterval(runFaceDetection, 400);
      };
      if (videoRef.current?.readyState >= 2 && videoRef.current?.videoWidth > 0) {
        startFaceDetectionLoop();
      } else if (videoRef.current) {
        videoRef.current.onloadeddata = () => {
          if (videoRef.current?.videoWidth > 0) startFaceDetectionLoop();
        };
      }
      setTimeout(() => {
        if (videoRef.current?.videoWidth > 0 && faceDetection && !faceDetectionLoopRef.current) {
          startFaceDetectionLoop();
        }
      }, 1000);

      // When camera feed is gone, clear camera + face and stop face detection loop
      watchIntervalRef.current = setInterval(() => {
        const video = videoRef.current;
        const vt = streamRef.current?.getVideoTracks()[0];
        const hasFeed = vt && vt.readyState === 'live' && vt.enabled && video?.videoWidth > 0 && video?.videoHeight > 0;
        if (!hasFeed) {
          setChecks(prev => ({ ...prev, camera: false, face: false }));
          if (faceDetectionLoopRef.current) {
            clearInterval(faceDetectionLoopRef.current);
            faceDetectionLoopRef.current = null;
          }
        }
      }, 500);

      setChecks(prev => ({ ...prev, internet: true }));
      const isSupported = /Chrome|Firefox|Safari|Edge/.test(navigator.userAgent);
      setChecks(prev => ({ ...prev, browser: isSupported }));
    } catch (error) {
      console.error('System check error:', error);
      if (error.name === 'NotAllowedError') {
        alert('Please allow camera and microphone access');
      }
    } finally {
      setChecking(false);
    }
  };

  const allChecksPassed = Object.values(checks).every(check => check === true);

  const enableScreenShare = async () => {
    setScreenShareBusy(true);
    setScreenShareError('');
    try {
      const displayStream = await requestEntireScreenShare();
      screenStreamRef.current?.getTracks?.().forEach((t) => t.stop());
      screenStreamRef.current = displayStream;
      registerInterviewPrepInstances({ screenStream: displayStream });
      setChecks((prev) => ({ ...prev, screenShare: true }));
      const track = displayStream.getVideoTracks()[0];
      if (track) {
        track.onended = () => {
          screenStreamRef.current = null;
          setChecks((prev) => ({ ...prev, screenShare: false }));
          setScreenShareError('Screen share stopped — please share your entire screen again.');
        };
      }
    } catch (err) {
      setChecks((prev) => ({ ...prev, screenShare: false }));
      setScreenShareError(
        err?.code === 'NOT_ENTIRE_SCREEN'
          ? err.message
          : 'Screen sharing is required. Please allow entire-screen share.'
      );
    } finally {
      setScreenShareBusy(false);
    }
  };

  const runMicTest = async () => {
    const sessionId = localStorage.getItem('session_id');
    if (!sessionId) return;

    setMicTest('playing');

    try {
      const apiBase = getPublicApiOrigin();
      const res = await fetch(`${apiBase}/api/v1/sessions/${sessionId}/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: "Hello! Can you hear me clearly? Please say something after I finish speaking to confirm your microphone is working." }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.audio) {
          const binary = atob(data.audio);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          const url = URL.createObjectURL(new Blob([bytes], { type: 'audio/mpeg' }));
          const audio = new Audio(url);
          await new Promise((resolve) => {
            audio.onended = resolve;
            audio.onerror = resolve;
            audio.play().catch(resolve);
          });
          URL.revokeObjectURL(url);
        }
      }
    } catch (_) {
      // If TTS fails, still proceed to the listening step
    }

    setMicTest('listening');

    await new Promise((resolve) => {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        setMicTest('passed');
        resolve();
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      let settled = false;
      const settle = (result) => {
        if (settled) return;
        settled = true;
        try { recognition.stop(); } catch (_) {}
        setMicTest(result);
        resolve();
      };

      const timeout = setTimeout(() => settle('failed'), 8000);

      recognition.onresult = () => { clearTimeout(timeout); settle('passed'); };
      recognition.onerror = () => { clearTimeout(timeout); settle('failed'); };
      recognition.onend = () => { clearTimeout(timeout); if (!settled) settle('failed'); };

      try { recognition.start(); } catch (_) { clearTimeout(timeout); settle('failed'); }
    });
  };

  const handleStartInterview = async () => {
    const sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
      alert('Session not found. Please try again.');
      window.location.reload();
      return;
    }
    if (!checks.screenShare || !isStreamLive(screenStreamRef.current)) {
      setScreenShareError('Share your entire screen before starting.');
      return;
    }

    setStarting(true);
    try {
      // Unlock AudioContext on user click so energy analyser can run unblocked in Chrome
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          const warmCtx = new AudioCtx();
          if (warmCtx.state === 'suspended') {
            await warmCtx.resume();
          }
          await warmCtx.close();
        }
      } catch (_) {}

      // Same user-gesture as the Start click — browsers allow fullscreen here.
      await requestExamFullscreen(document.documentElement);
      if (onStartInterview) {
        onStartInterview({
          cameraStream: streamRef.current,
          screenStream: screenStreamRef.current,
        });
      }
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="candidate-landing system-check-page">
      <div className="system-check-container">
        <h2>System Check</h2>
        <p className="subtitle">We need to verify your setup before starting</p>

        <div className="system-check-content">
          {/* Left Side - Camera Preview (50%) */}
          <div className="system-check-left">
            <div className="camera-preview">
              <video ref={videoRef} autoPlay muted playsInline className="video-preview" />
              {/* Face status is shown in the checklist on the right — keep the preview unobstructed. */}
            </div>
          </div>

          {/* Right Side - Check List (50%) */}
          <div className="system-check-right">
            <div className="check-list">
              <div className={`check-item ${checks.camera ? 'passed' : 'failed'}`}>
                <span className="check-icon">{checks.camera ? '✅' : '❌'}</span>
                <span>Camera Access: {checks.camera ? 'Granted' : 'Not Granted'}</span>
              </div>

              <div className={`check-item ${checks.microphone ? 'passed' : 'failed'}`}>
                <span className="check-icon">{checks.microphone ? '✅' : '❌'}</span>
                <span>Microphone Access: {checks.microphone ? 'Granted' : 'Not Granted'}</span>
              </div>

              <div className={`check-item ${checks.internet ? 'passed' : 'failed'}`}>
                <span className="check-icon">{checks.internet ? '✅' : '❌'}</span>
                <span>Internet Connection: {checks.internet ? 'Stable' : 'Unstable'}</span>
              </div>

              <div className={`check-item ${checks.browser ? 'passed' : 'failed'}`}>
                <span className="check-icon">{checks.browser ? '✅' : '❌'}</span>
                <span>Browser: {checks.browser ? 'Supported' : 'Not Supported'}</span>
              </div>

              <div className={`check-item ${checks.face ? 'passed' : 'failed'}`}>
                <span className="check-icon">{checks.face ? '✅' : '⏳'}</span>
                <span>Face visible: {checks.face ? 'Yes' : 'Show your face in the camera'}</span>
              </div>

              <div className={`check-item ${checks.screenShare ? 'passed' : 'failed'}`}>
                <span className="check-icon">{checks.screenShare ? '✅' : '❌'}</span>
                <span>
                  Screen share: {checks.screenShare ? 'Entire screen shared' : 'Required — share entire screen'}
                </span>
              </div>
              {!checks.screenShare && !checking && (
                <button
                  type="button"
                  onClick={enableScreenShare}
                  disabled={screenShareBusy}
                  style={{
                    marginTop: 4,
                    marginBottom: 8,
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: 8,
                    border: '1.5px solid #6366F1',
                    background: screenShareBusy ? '#e0e7ff' : 'white',
                    color: '#6366F1',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: screenShareBusy ? 'wait' : 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {screenShareBusy ? 'Waiting for screen share…' : 'Share Entire Screen'}
                </button>
              )}
              {screenShareError && (
                <div style={{ fontSize: 12, color: '#b91c1c', marginBottom: 8 }}>{screenShareError}</div>
              )}
            </div>

            {checking && (
              <div className="checking-message">
                <div className="spinner"></div>
                <p>Running system checks...</p>
              </div>
            )}

            {!checking && allChecksPassed && (
              <div className="all-ready">
                <p className="ready-message">✅ All systems ready!</p>
              </div>
            )}

            {!checking && !allChecksPassed && (
              <div className="not-ready">
                <p className="error-message">⚠️ Please fix the issues above before proceeding</p>
              </div>
            )}

            {!checking && allChecksPassed && (
              <div style={{
                marginTop: 14,
                padding: '14px 16px',
                borderRadius: 10,
                border: micTest === 'passed' ? '1.5px solid #34d399' : micTest === 'failed' ? '1.5px solid #fbbf24' : '1.5px solid #e5e7eb',
                background: micTest === 'passed' ? '#f0fdf4' : micTest === 'failed' ? '#fffbeb' : '#f9fafb',
                transition: 'border-color .2s, background .2s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 22, lineHeight: 1 }}>
                      {micTest === 'passed' ? '✅' : micTest === 'failed' ? '⚠️' : '🎙️'}
                    </span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#374151' }}>
                        {micTest === 'idle' && 'Test your microphone with David'}
                        {micTest === 'playing' && 'David is speaking — listen carefully…'}
                        {micTest === 'listening' && 'Now say something…'}
                        {micTest === 'passed' && 'Microphone is working!'}
                        {micTest === 'failed' && "We couldn't hear you"}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        {micTest === 'idle' && 'David will say a phrase, then speak to confirm your mic is audible'}
                        {micTest === 'playing' && 'Audio is playing…'}
                        {micTest === 'listening' && 'Listening for up to 8 seconds…'}
                        {micTest === 'passed' && 'Your voice is coming through clearly'}
                        {micTest === 'failed' && 'Check your microphone settings and try again'}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={runMicTest}
                    disabled={micTest === 'playing' || micTest === 'listening'}
                    style={{
                      flexShrink: 0,
                      padding: '7px 14px',
                      borderRadius: 8,
                      border: '1.5px solid #6366F1',
                      background: micTest === 'playing' || micTest === 'listening' ? '#e0e7ff' : 'white',
                      color: '#6366F1',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: micTest === 'playing' || micTest === 'listening' ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {micTest === 'idle' ? '▶ Test Mic' : micTest === 'playing' || micTest === 'listening' ? '…Testing' : '↺ Retry'}
                  </button>
                </div>
                {micTest === 'listening' && (
                  <div style={{ marginTop: 10, height: 4, borderRadius: 2, background: '#e5e7eb', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      borderRadius: 2,
                      background: '#6366F1',
                      animation: 'micTestProgress 8s linear forwards',
                    }} />
                  </div>
                )}
              </div>
            )}

            <div className="system-check-actions">
              <button
                type="button"
                onClick={handleStartInterview}
                className="btn-large"
                disabled={!allChecksPassed || checking || starting}
                style={{
                  width: '100%',
                  padding: '15px 32px',
                  fontSize: 15,
                  fontWeight: 600,
                  color: 'white',
                  background: !allChecksPassed || checking || starting ? '#C7D2FE' : 'linear-gradient(135deg, #6366F1, #4F46E5)',
                  border: 'none',
                  borderRadius: 10,
                  cursor: !allChecksPassed || checking || starting ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: !allChecksPassed || checking || starting ? 'none' : '0 4px 14px rgba(99,102,241,.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'all .18s',
                  opacity: !allChecksPassed || checking || starting ? 0.55 : 1,
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="16" height="16" aria-hidden>
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                {starting ? 'Starting…' : 'Start Interview'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateLanding;
